import type { OverlayTransport } from '@shared/lib/ws'
import type { OverlayEvent } from '@shared/types'
import type {
  AckMessage,
  PluginNotAvailableMessage,
  RegisterPluginMessage,
  UpdateI18nMessage,
  UpdatePluginSoundMessage,
} from '@shared/types/ws'

import { sessionService } from '@entities/session'
import {
  CROWD_AVATARS_EVENT_TYPE,
  useCrowdAvatarsStore,
  type AvatarAppearance,
} from '@features/crowd-avatars'
import { deferredEventBuffer, pendingPluginRequestTracker } from '@features/deferred-buffer'
import { dynamicPluginLoader } from '@features/dynamic-plugin'
import { useEffectQueue } from '@features/effect-queue'
import { OVERLAY_CONFIG } from '@shared/config'
import { effectRegistry } from '@shared/lib/effect-registry'
import { SocketIoTransport } from '@shared/lib/ws'
import { useEffect, useRef } from 'react'

import { useWsConnectionStore } from './store'

interface Options {
  streamerId: string
  /** Открывать соединение только когда сессия готова (cookie + ключ). */
  enabled?: boolean
}

/**
 * Полный жизненный цикл события:
 *
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ событие получено                                                 │
 * │   ├─ плагин зарегистрирован? ─ДА──► ack(processing) → enqueue    │
 * │   └─ плагина нет?      ──────── ack(deferred)                    │
 * │          │                      → deferredBuffer.defer()         │
 * │          └─ уже запрошен? ──ДА──► ждём (dedup)                   │
 * │                            ──НЕТ─► plugin_request                │
 * │                                                                  │
 * │ получен register_plugin ───────► load → plugin_ready             │
 * │          └─ plugin_ready ──────► flush deferred → enqueue всех   │
 * │                                                                  │
 * │ plugin_not_available ──────────► drop deferred → ack(skipped)    │
 * │                                                                  │
 * │ reconnect ─────────────────────► clear pendingTracker            │
 * │          └─ deferredBuffer.types() → повторный plugin_request    │
 * └──────────────────────────────────────────────────────────────────┘
 */
export function useWsConnection({ streamerId, enabled = true }: Options) {
  const rwsRef = useRef<OverlayTransport | null>(null)

  const setStatusRef = useRef(useWsConnectionStore.getState().setStatus)
  const setPongRef   = useRef(useWsConnectionStore.getState().setPongTime)
  const enqueueRef   = useRef(useEffectQueue.getState().enqueue)

  useEffect(() => {
    if (!enabled) return
    // ── Вспомогательные функции ──────────────────────────────────────────────

    /** Отправляет plugin_request если тип ещё не запрошен */
    function requestPlugin(rws: OverlayTransport, pluginType: string): void {
      const shouldSend = pendingPluginRequestTracker.request(pluginType)
      if (shouldSend) {
        rws.send({ type: 'plugin_request', pluginType })
      }
    }

    /** Обработка нового overlay-события: enqueue или deferred */
    function handleOverlayEvent(rws: OverlayTransport, event: OverlayEvent): void {
      // Толпа аватаров — постоянный слой, не effect-queue
      if (event.type === CROWD_AVATARS_EVENT_TYPE) {
        const ack: AckMessage = {
          type: 'ack',
          eventId: event.id,
          status: 'processing',
          receivedAt: Date.now(),
        }
        rws.send(ack)
        const payload = event.payload as {
          action?: string
          avatarId?: string
          displayName?: string
          message?: string
          appearance?: AvatarAppearance
          configOnly?: boolean
          overlayConfig?: { sfxEnabled?: boolean }
        }
        if (typeof payload.overlayConfig?.sfxEnabled === 'boolean') {
          useCrowdAvatarsStore
            .getState()
            .setSfxEnabled(payload.overlayConfig.sfxEnabled)
        }
        if (payload.configOnly) return

        const action =
          payload.action === 'throw_grenade'
            ? 'throw_grenade'
            : payload.action === 'throw_heart'
              ? 'throw_heart'
              : payload.action === 'smash_glass'
                ? 'smash_glass'
                : 'throw_poop'
        useCrowdAvatarsStore.getState().enqueueAction({
          id: event.id,
          avatarId: payload.avatarId ?? '',
          displayName: payload.displayName,
          action,
          message: payload.message,
          timestamp: event.timestamp,
          appearance: payload.appearance,
        })
        return
      }

      // Плагин зарегистрирован — стандартный путь
      if (effectRegistry.has(event.type)) {
        const ack: AckMessage = {
          type: 'ack',
          eventId: event.id,
          status: 'processing',
          receivedAt: Date.now(),
        }
        rws.send(ack)
        enqueueRef.current(event)
        return
      }

      // Плагин не готов — откладываем событие, запрашиваем плагин
      const ack: AckMessage = {
        type: 'ack',
        eventId: event.id,
        status: 'deferred',
        receivedAt: Date.now(),
      }
      rws.send(ack)
      deferredEventBuffer.defer(event)
      requestPlugin(rws, event.type)
    }

    /** После plugin_ready — replay всех отложенных событий этого типа */
    function replayDeferred(pluginType: string): void {
      const ttl = OVERLAY_CONFIG.QUEUE_PENDING_TTL_MS
      const events = deferredEventBuffer.flush(pluginType, ttl)
      events.forEach((e) => enqueueRef.current(e))
    }

    /** После plugin_not_available — дропаем буфер, шлём skipped ack */
    function handlePluginNotAvailable(
      rws: OverlayTransport,
      msg: PluginNotAvailableMessage
    ): void {
      const { pluginType, reason = 'plugin_not_available' } = msg
      pendingPluginRequestTracker.resolve(pluginType)

      // Достаём все зависшие события чтобы отправить skipped ack
      const ttl = OVERLAY_CONFIG.QUEUE_PENDING_TTL_MS
      const dropped = deferredEventBuffer.flush(pluginType, ttl)

      dropped.forEach((event) => {
        const ack: AckMessage = {
          type: 'ack',
          eventId: event.id,
          status: 'skipped',
          receivedAt: Date.now(),
          reason,
        }
        rws.send(ack)
      })
    }

    /** При реконнекте: сбрасываем pending-трекер, повторно запрашиваем
     *  все типы у которых есть отложенные события в буфере. */
    function handleReconnect(rws: OverlayTransport): void {
      // Сбрасываем pending Set — новое соединение, старые запросы потеряны
      pendingPluginRequestTracker.clear()

      // Повторно запрашиваем все типы из deferredBuffer
      const deferredTypes = deferredEventBuffer.types()
      deferredTypes.forEach((pluginType) => requestPlugin(rws, pluginType))
    }

    // ── Создание соединения ───────────────────────────────────────────────────

    const rws = new SocketIoTransport(OVERLAY_CONFIG.WS_BASE_URL, streamerId, {
      baseRetryDelay: OVERLAY_CONFIG.WS_RECONNECT_BASE_MS,
      maxRetryDelay:  OVERLAY_CONFIG.WS_RECONNECT_MAX_MS,
      pingIntervalMs: OVERLAY_CONFIG.WS_PING_INTERVAL_MS,
      onStatus: (s) => setStatusRef.current(s),
      onReconnect: () => handleReconnect(rws),
      // При истечении сессии — пробуем обновить cookie и переподключиться
      onAuthError: () => sessionService.refreshSession(),
      onMessage: (raw: string) => {
        let msg: Record<string, unknown>
        try { msg = JSON.parse(raw) as Record<string, unknown> }
        catch { return }

        const msgType = msg['type'] as string | undefined

        // ── Pong ─────────────────────────────────────────────────────────────
        if (msgType === 'pong') {
          setPongRef.current(Date.now())
          return
        }

        // ── register_plugin: загружаем, после plugin_ready — replay deferred ─
        if (msgType === 'register_plugin') {
          dynamicPluginLoader.setStatusCallback((statusMsg) => {
            rws.send(statusMsg)
            // После успешной регистрации — replay всех отложенных событий
            if (statusMsg.type === 'plugin_ready') {
              pendingPluginRequestTracker.resolve(statusMsg.pluginType)
              replayDeferred(statusMsg.pluginType)
            }
            // При отклонении — тоже снимаем ожидание (позволит повторный запрос)
            if (statusMsg.type === 'plugin_rejected') {
              pendingPluginRequestTracker.resolve(statusMsg.pluginType)
            }
          })
          void dynamicPluginLoader.load(msg as unknown as RegisterPluginMessage)
          return
        }

        // ── plugin_not_available: пропускаем все отложенные события типа ─────
        if (msgType === 'plugin_not_available') {
          // eslint-disable-next-line no-console
          console.warn(`[overlay] plugin_not_available: ${msg['pluginType']}`, msg)
          handlePluginNotAvailable(rws, msg as unknown as PluginNotAvailableMessage)
          return
        }

        // ── update_plugin_sound ───────────────────────────────────────────────
        if (msgType === 'update_plugin_sound') {
          const { pluginType, sound } = msg as unknown as UpdatePluginSoundMessage
          dynamicPluginLoader.updateSound(pluginType, sound)
          return
        }

        // ── update_i18n: смена языка стримера — обновляем словарь без reload ──
        if (msgType === 'update_i18n') {
          const { pluginType, locale, i18n } = msg as unknown as UpdateI18nMessage
          dynamicPluginLoader.updateI18n(pluginType, locale, i18n)
          return
        }

        // ── Overlay-событие ───────────────────────────────────────────────────
        const event = msg as unknown as OverlayEvent
        if (!event.id || !event.type) return
        handleOverlayEvent(rws, event)
      },
    })

    rwsRef.current = rws
    rws.connect()

    return () => {
      rws.destroy()
      rwsRef.current = null
    }
  }, [streamerId, enabled])
}
